import React from 'react';
import { StyleSheet, TextInput, TouchableOpacity, View } from 'react-native';

import { handleAuthError } from '@auth/AuthErrorHandler';
import {
  createUserWithEmailAndPassword,
  validateUserEmail,
  validateUserPassword,
} from '@auth/EmailAndPassword';
import { sendEmailVerification, updateName } from '@auth/ManageUser';
import { TextDivider } from '@components/Divider';
import {
  AppleSignInButton,
  FacebookSignInButton,
  GoogleSignInButton,
} from '@components/FederatedSignInButton';
import { createStringValidator, submitFormSafely, useAppForm } from '@components/Form';
import { BackButton } from '@components/IconButton';
import ScreenContainer from '@components/ScreenContainer';
import { Headline, Paragraph } from '@components/Typography';
import { joinUserDisplayName, validateUserDisplayName } from '@db/Users';
import { openCookiesPolicy, openPrivacyPolicy, openTermsPolicy } from '@helpers/Linking';
import { AuthRoute } from '@navigation/Routes';
import { RegisterScreenProps } from '@navigation/Types';

type RegisterFormValues = {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
};

export default function RegisterScreen({ navigation }: RegisterScreenProps) {
  const lastNameRef = React.useRef<TextInput>(null);
  const emailRef = React.useRef<TextInput>(null);
  const passwordRef = React.useRef<TextInput>(null);

  const registerActions = async (values: RegisterFormValues) => {
    const displayName = joinUserDisplayName(values);

    const { user: currentUser } = await createUserWithEmailAndPassword(
      values.email.trim(),
      values.password
    );
    await updateName(currentUser, displayName);
    await sendEmailVerification(currentUser);
  };

  const form = useAppForm({
    defaultValues: {
      email: '',
      firstName: '',
      lastName: '',
      password: '',
    } satisfies RegisterFormValues,
    onSubmit: async ({ value }) => {
      try {
        await registerActions(value);
      } catch (error) {
        handleAuthError(error);
        throw error;
      }
    },
  });

  const onSignUpPressed = () => submitFormSafely(form);

  const renderFederatedSignInButtons = () => (
    <>
      <AppleSignInButton />
      <GoogleSignInButton />
      <FacebookSignInButton />
      <TextDivider text="OR" />
    </>
  );

  const renderFirstNameInput = () => (
    <form.AppField
      name="firstName"
      validators={{ onChange: createStringValidator(validateUserDisplayName) }}>
      {(field) => (
        <field.TextInput
          label="First name"
          returnKeyType="next"
          autoCorrect={false}
          autoComplete="given-name"
          textContentType="givenName"
          onSubmitEditing={() => lastNameRef.current?.focus()}
          testID="screens.RegisterScreen.TextInput.FirstName"
        />
      )}
    </form.AppField>
  );

  const renderLastNameInput = () => (
    <form.AppField
      name="lastName"
      validators={{ onChange: createStringValidator(validateUserDisplayName) }}>
      {(field) => (
        <field.TextInput
          inputRef={lastNameRef}
          label="Last name"
          returnKeyType="next"
          autoCorrect={false}
          autoComplete="family-name"
          textContentType="familyName"
          onSubmitEditing={() => emailRef.current?.focus()}
          testID="screens.RegisterScreen.TextInput.LastName"
        />
      )}
    </form.AppField>
  );

  const renderEmailInput = () => (
    <form.AppField name="email" validators={{ onChange: createStringValidator(validateUserEmail) }}>
      {(field) => (
        <field.EmailInput
          inputRef={emailRef}
          label="Email"
          returnKeyType="next"
          onSubmitEditing={() => passwordRef.current?.focus()}
          testID="screens.RegisterScreen.TextInput.Email"
        />
      )}
    </form.AppField>
  );

  const renderPasswordInput = () => (
    <form.AppField
      name="password"
      validators={{ onChange: createStringValidator(validateUserPassword) }}>
      {(field) => (
        <field.PasswordInput
          inputRef={passwordRef}
          label="Password"
          returnKeyType="done"
          onSubmitEditing={onSignUpPressed}
          testID="screens.RegisterScreen.TextInput.Password"
        />
      )}
    </form.AppField>
  );

  const renderPolicyLinks = () => (
    <View style={styles.hyperlinkContainer}>
      <Paragraph>By signing up, you agree to our </Paragraph>
      <TouchableOpacity onPress={openTermsPolicy}>
        <Paragraph style={styles.link}>Terms of Use, </Paragraph>
      </TouchableOpacity>
      <TouchableOpacity onPress={openPrivacyPolicy}>
        <Paragraph style={styles.link}>Privacy Policy, </Paragraph>
      </TouchableOpacity>
      <Paragraph>and </Paragraph>
      <TouchableOpacity onPress={openCookiesPolicy}>
        <Paragraph style={styles.link}>Cookie Policy.</Paragraph>
      </TouchableOpacity>
    </View>
  );

  const renderLoginLink = () => (
    <View style={styles.row}>
      <Paragraph>Already have an account? </Paragraph>
      <TouchableOpacity
        onPress={() => navigation.replace(AuthRoute.Login)}
        testID="screens.RegisterScreen.Button.Login">
        <Paragraph style={styles.link}>Login</Paragraph>
      </TouchableOpacity>
    </View>
  );

  return (
    <form.AppForm>
      <ScreenContainer scrollable testID="screens.RegisterScreen.Background">
        <BackButton onPress={navigation.goBack} testID="screens.RegisterScreen.BackButton" />
        <View style={styles.container}>
          <Headline style={styles.title}>Sign up for Joinstar</Headline>
          {renderFederatedSignInButtons()}
          {renderFirstNameInput()}
          {renderLastNameInput()}
          {renderEmailInput()}
          {renderPasswordInput()}
          <form.SubmitBigButton
            mode="contained"
            style={styles.button}
            testID="screens.RegisterScreen.Button.Register">
            Sign Up
          </form.SubmitBigButton>
          {renderPolicyLinks()}
          {renderLoginLink()}
        </View>
      </ScreenContainer>
    </form.AppForm>
  );
}

const styles = StyleSheet.create({
  button: {
    marginTop: 24,
  },
  container: {
    paddingHorizontal: 20,
  },
  hyperlinkContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'center',
    marginTop: 5,
  },
  link: {
    fontWeight: 'bold',
  },
  row: {
    flexDirection: 'row',
    justifyContent: 'center',
    marginTop: 10,
  },
  title: {
    paddingBottom: 5,
    textAlign: 'center',
  },
});
